import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';

import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';

import { getApiBaseUrl } from '../utils/backendBaseUrl';
import { appPath } from '../utils/appRouting';
import BoardDesk from '../components/boards/BoardDesk';

const API = getApiBaseUrl();

function safeCode(raw) {
  return (raw || '').trim().toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 12);
}

export default function BachecaPubblica({ fullBleed = false } = {}) {
  const navigate = useNavigate();
  const location = useLocation();
  const { code: rawCode } = useParams();
  const code = useMemo(() => safeCode(rawCode), [rawCode]);

  const isFull = useMemo(() => {
    if (fullBleed) return true;
    try {
      const sp = new URLSearchParams(location.search || '');
      return sp.get('full') === '1' || sp.get('full') === 'true';
    } catch {
      return false;
    }
  }, [fullBleed, location.search]);

  const [loading, setLoading] = useState(true);
  const [board, setBoard] = useState(null);
  const [posts, setPosts] = useState([]);

  const [name, setName] = useState(() => {
    try {
      return (window.localStorage.getItem('board_name') || '').trim().slice(0, 60);
    } catch {
      return '';
    }
  });

  const [text, setText] = useState('');
  const [column, setColumn] = useState('');
  const [sending, setSending] = useState(false);

  const [uploads, setUploads] = useState([]); // {upload_id,url,is_image,content_type,size}
  const fileInputRef = useRef(null);

  const [composerOpen, setComposerOpen] = useState(false);

  const canPost = useMemo(() => {
    if (!board) return false;
    if (!name.trim()) return false;
    if (!text.trim()) return false;
    if (board?.settings?.read_only) return false;
    return true;
  }, [board, name, text]);

  async function resolveBoard({ silent } = { silent: false }) {
    if (!/^[A-Z0-9]{4,12}$/.test(code)) {
      setLoading(false);
      toast.error('Codice non valido');
      return;
    }

    setLoading(true);
    try {
      const res = await axios.post(`${API}/boards/public/resolve/${encodeURIComponent(code)}`, { name: name || null }, { timeout: 12000 });
      setBoard(res.data?.board || null);
      setPosts(res.data?.posts || []);

      if (res.data?.board?.board_type === 'columns') {
        const cols = res.data?.board?.columns || [];
        if (!column && cols.length > 0) setColumn(cols[0]);
      }
    } catch (e) {
      if (!silent) toast.error(e?.response?.data?.detail || e?.message || 'Bacheca non disponibile');
      setBoard(null);
      setPosts([]);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    resolveBoard();
  }, [code]);

  useEffect(() => {
    if (!board?.settings?.realtime) return;
    const id = window.setInterval(() => {
      resolveBoard({ silent: true });
    }, 5000);
    return () => window.clearInterval(id);
  }, [board?.settings?.realtime, code]);

  function persistName(next) {
    const cleaned = (next || '').trim().slice(0, 60);
    setName(cleaned);
    try {
      window.localStorage.setItem('board_name', cleaned);
    } catch {
      // ignore
    }
  }

  async function doUpload(file) {
    const form = new FormData();
    form.append('file', file);

    const res = await axios.post(`${API}/boards/public/${encodeURIComponent(code)}/upload`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 25000
    });
    return res.data;
  }

  async function onPickFiles(e) {
    const files = Array.from(e.target.files || []).slice(0, 4);
    if (files.length === 0) return;

    try {
      for (const f of files) {
        const uploaded = await doUpload(f);
        setUploads((u) => [...u, uploaded].slice(0, 4));
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || err?.message || 'Upload non riuscito');
    } finally {
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  }

  async function submitPost() {
    if (!canPost) return;

    setSending(true);
    try {
      const payload = {
        name: name.trim(),
        text: text.trim(),
        column: board?.board_type === 'columns' ? (column || null) : null,
        upload_ids: uploads.map((u) => u.upload_id).filter(Boolean)
      };

      const res = await axios.post(`${API}/boards/public/${encodeURIComponent(code)}/posts`, payload, { timeout: 12000 });

      setText('');
      setUploads([]);

      if (res.data?.status === 'pending') {
        toast.success('Messaggio inviato: in attesa di approvazione');
      } else {
        toast.success('Messaggio pubblicato');
      }

      await resolveBoard({ silent: true });
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || 'Invio non riuscito');
    } finally {
      setSending(false);
    }
  }

  if (loading && !board) {
    return (
      <div className="h-[100dvh] overflow-y-auto bg-background text-foreground flex items-center justify-center p-6">
        <div className="w-full max-w-xl text-sm text-muted-foreground">Caricamento…</div>
      </div>
    );
  }

  if (!board) {
    return (
      <div className="h-[100dvh] overflow-y-auto bg-background text-foreground flex items-center justify-center p-6">
        <Card className="w-full max-w-xl">
          <CardContent className="p-6">
            <div className="text-lg font-semibold">Bacheca non disponibile</div>
            <div className="mt-1 text-sm text-muted-foreground">Controlla il codice oppure chiedi al docente un nuovo link.</div>
            <Button className="mt-4" onClick={() => navigate(appPath('/'))}> 
              Vai al sito
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const readOnly = !!board?.settings?.read_only;
  const requireApproval = !!board?.settings?.require_approval;

  if (isFull) {
    return (
      <div className="h-[100dvh] bg-background text-foreground flex flex-col">
        <div className="h-14 border-b border-border bg-background/80 backdrop-blur-md flex items-center justify-between px-3 md:px-4 gap-2">
          <div className="min-w-0">
            <div className="text-sm font-semibold truncate">{board.title || 'Bacheca'}</div>
            <div className="text-[11px] text-muted-foreground truncate">
              Codice: <span className="font-mono">{board.public_code}</span>
              {readOnly ? ' • sola lettura' : ''}
              {requireApproval ? ' • moderata' : ' • senza moderazione'}
            </div>
          </div>

          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => resolveBoard()} disabled={loading}>
              Aggiorna
            </Button>
            {!readOnly ? (
              <Button size="sm" onClick={() => setComposerOpen(true)}>
                Scrivi
              </Button>
            ) : null}
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                setComposerOpen(false);
                navigate(appPath(`/b/${code}`), { replace: true });
              }}
            >
              Esci
            </Button>
          </div>
        </div>

        <div className="flex-1 min-h-0">
          {posts.length === 0 ? (
            <div className="h-full flex items-center justify-center text-sm text-muted-foreground px-6">
              Nessun contributo ancora.
            </div>
          ) : (
            <BoardDesk posts={posts} height="100%" frame={false} />
          )}
        </div>

        {composerOpen ? (
          <div className="fixed inset-0 z-50">
            <button
              type="button"
              className="absolute inset-0 bg-black/40"
              onClick={() => setComposerOpen(false)}
              aria-label="Chiudi"
            />
            <div className="absolute inset-x-0 top-16 px-4">
              <div className="mx-auto w-full max-w-xl">
                <Card className="shadow-xl">
                  <CardContent className="p-5">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="text-lg font-semibold">Scrivi un contributo</div>
                      <div className="mt-1 text-xs text-muted-foreground">
                        {requireApproval
                          ? 'Il docente approverà i messaggi prima della pubblicazione.'
                          : 'Il messaggio viene pubblicato subito.'}
                      </div>
                    </div>
                    <Button variant="outline" size="sm" onClick={() => setComposerOpen(false)}>
                      Chiudi
                    </Button>
                  </div>

                  <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div>
                      <div className="text-sm font-medium mb-1">Il tuo nome</div>
                      <Input value={name} onChange={(e) => persistName(e.target.value)} placeholder="Es. Marco" maxLength={60} disabled={readOnly} />
                    </div>

                    {board.board_type === 'columns' ? (
                      <div>
                        <div className="text-sm font-medium mb-1">Colonna</div>
                        <select
                          className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm"
                          value={column}
                          onChange={(e) => setColumn(e.target.value)}
                          disabled={readOnly}
                        >
                          {(board.columns || []).map((c) => (
                            <option key={c} value={c}>{c}</option>
                          ))}
                        </select>
                      </div>
                    ) : null}

                    <div className="md:col-span-2">
                      <div className="text-sm font-medium mb-1">Messaggio</div>
                      <Textarea value={text} onChange={(e) => setText(e.target.value)} placeholder="Scrivi qui…" maxLength={1500} disabled={readOnly} />
                    </div>

                    <div className="md:col-span-2">
                      <div className="flex items-center justify-between gap-2">
                        <div>
                          <div className="text-sm font-medium">Allegati (opzionale)</div>
                          <div className="text-xs text-muted-foreground">Max 4 allegati; immagini 5MB, file 10MB.</div>
                        </div>
                        <div>
                          <input ref={fileInputRef} type="file" multiple onChange={onPickFiles} disabled={readOnly} />
                        </div>
                      </div>

                      {uploads.length > 0 ? (
                        <div className="mt-2 flex flex-wrap gap-2">
                          {uploads.map((u) => (
                            <button
                              key={u.upload_id}
                              type="button"
                              className="text-xs rounded-full border border-border px-3 py-1 hover:bg-accent/40"
                              onClick={() => setUploads((all) => all.filter((x) => x.upload_id !== u.upload_id))}
                            >
                              {u.is_image ? 'Immagine' : 'File'} • rimuovi
                            </button>
                          ))}
                        </div>
                      ) : null}
                    </div>
                  </div>

                  <div className="mt-4 flex gap-2">
                    <Button
                      onClick={async () => {
                        await submitPost();
                        setComposerOpen(false);
                      }}
                      disabled={!canPost || sending}
                    >
                      {sending ? 'Invio…' : 'Invia'}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
        ) : null}
      </div>
    );
  }

  return (
    <div className="h-[100dvh] overflow-y-auto bg-background text-foreground scroll-smooth">
      <div className="max-w-4xl mx-auto p-4 md:p-6">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold">{board.title || 'Bacheca'}</h1>
            {board.description ? (
              <div className="mt-1 text-sm text-muted-foreground whitespace-pre-wrap">{board.description}</div>
            ) : null}
            <div className="mt-2 flex flex-wrap gap-2">
              <Badge variant="secondary">Codice: <span className="font-mono ml-1">{board.public_code}</span></Badge>
              {readOnly ? <Badge variant="secondary">Sola lettura</Badge> : null}
              {requireApproval ? <Badge variant="outline">Moderata</Badge> : <Badge variant="outline">Senza moderazione</Badge>}
            </div>
          </div>
          <Button variant="outline" onClick={() => resolveBoard()} disabled={loading}>Aggiorna</Button>
        </div>

        <Card className="mt-6">
          <CardContent className="p-5">
            <div className="text-lg font-semibold">Scrivi un contributo</div>
            <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <div className="text-sm font-medium mb-1">Il tuo nome</div>
                <Input value={name} onChange={(e) => persistName(e.target.value)} placeholder="Es. Marco" maxLength={60} disabled={readOnly} />
              </div>
              {board.board_type === 'columns' ? (
                <div>
                  <div className="text-sm font-medium mb-1">Colonna</div>
                  <select
                    className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm"
                    value={column}
                    onChange={(e) => setColumn(e.target.value)}
                    disabled={readOnly}
                  >
                    {(board.columns || []).map((c) => (
                      <option key={c} value={c}>{c}</option>
                    ))}
                  </select>
                </div>
              ) : null}
              <div className="md:col-span-2">
                <div className="text-sm font-medium mb-1">Messaggio</div>
                <Textarea value={text} onChange={(e) => setText(e.target.value)} placeholder="Scrivi qui…" maxLength={1500} disabled={readOnly} />
                <div className="mt-2 text-xs text-muted-foreground">
                  {requireApproval ? 'Il docente approverà i messaggi prima della pubblicazione.' : 'Il messaggio viene pubblicato subito.'}
                </div>
              </div>

              <div className="md:col-span-2">
                <div className="flex items-center justify-between gap-2">
                  <div>
                    <div className="text-sm font-medium">Allegati (opzionale)</div>
                    <div className="text-xs text-muted-foreground">Max 4 allegati; immagini 5MB, file 10MB.</div>
                  </div>
                  <div>
                    <input ref={fileInputRef} type="file" multiple onChange={onPickFiles} disabled={readOnly} />
                  </div>
                </div>

                {uploads.length > 0 ? (
                  <div className="mt-2 flex flex-wrap gap-2">
                    {uploads.map((u) => (
                      <button
                        key={u.upload_id}
                        type="button"
                        className="text-xs rounded-full border border-border px-3 py-1 hover:bg-accent/40"
                        onClick={() => setUploads((all) => all.filter((x) => x.upload_id !== u.upload_id))}
                      >
                        {u.is_image ? 'Immagine' : 'File'} • rimuovi
                      </button>
                    ))}
                  </div>
                ) : null}
              </div>
            </div>

            <div className="mt-4">
              <Button onClick={submitPost} disabled={!canPost || sending}>
                {readOnly ? 'Bacheca in sola lettura' : sending ? 'Invio…' : 'Invia'}
              </Button>
            </div>
          </CardContent>
        </Card>

        <div className="mt-6">
          <div className="flex items-center justify-between">
            <div className="text-lg font-semibold">Contributi</div>
            <Badge variant="secondary">{posts.length}</Badge>
          </div>

          {posts.length === 0 ? (
            <div className="mt-3 text-sm text-muted-foreground">Nessun contributo ancora.</div>
          ) : (
            <div className="mt-3">
              <BoardDesk
                posts={posts}
                height="65vh"
                title="Scrivania"
                hint="Trascina lo sfondo per muoverti nella bacheca"
              />
            </div>
          )}
        </div>

        <div className="mt-10 text-xs text-muted-foreground">
          Questa bacheca è pensata per uso didattico: niente profili, niente like, niente chat privata.
        </div>
      </div>
    </div>
  );
}
